package com.islamicmedia.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.islamicmedia.shared.domain.model.IslamicTrivia
import com.islamicmedia.shared.domain.model.TriviaCard
import com.islamicmedia.shared.domain.model.TriviaDifficulty
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Islamic Trivia (KIDZONE, 8 and over): pick Easy / Medium / Hard, then 10 questions that get harder as the
 * round goes, each with a short fact after the answer. Every round draws new questions from the vetted bank
 * (shared [IslamicTrivia]). Mirrors the app's IslamicTriviaGame; styles are in KidsStyles (.kids-trivia).
 */
@Composable
fun IslamicTriviaGame() {
    var difficulty by remember { mutableStateOf<TriviaDifficulty?>(null) }
    var cards by remember { mutableStateOf(emptyList<TriviaCard>()) }
    var index by remember { mutableStateOf(0) }
    var chosen by remember { mutableStateOf<Int?>(null) }
    var score by remember { mutableStateOf(0) }

    fun start(d: TriviaDifficulty) {
        difficulty = d; cards = IslamicTrivia.round(d); index = 0; chosen = null; score = 0
    }

    Div({ classes("kids-trivia") }) {
        val level = difficulty
        when {
            level == null || cards.isEmpty() -> {
                Div({ classes("h") }) { Text("Pick your level") }
                Div({ classes("sub") }) { Text("The questions get harder as you go!") }
                TriviaDifficulty.entries.forEachIndexed { i, d ->
                    Button({ classes("chunky", d.name.lowercase()); onClick { start(d) } }) {
                        Span({ classes("grow") }) { Text(d.label) }
                        Span { Text("★".repeat(i + 1)) }
                    }
                }
            }
            index >= cards.size -> {
                Div({ classes("h", "big") }) { Text("You got $score out of ${cards.size}!") }
                Div({ classes("sub") }) {
                    Text(when {
                        score == cards.size -> "Perfect! MashaAllah!"
                        score * 10 >= cards.size * 7 -> "Great job! MashaAllah!"
                        score * 2 >= cards.size -> "Well done! Keep learning!"
                        else -> "Good try! Every round you learn more."
                    })
                }
                Button({ classes("chunky", level.name.lowercase(), "center"); onClick { start(level) } }) {
                    Text("Play again (${level.label})")
                }
                Button({ classes("chunky", "next", "center"); onClick { difficulty = null } }) { Text("Pick another level") }
            }
            else -> {
                val card = cards[index]
                Div({ classes("top") }) {
                    Span({ classes("count") }) { Text("Question ${index + 1} of ${cards.size}") }
                    Span({ classes("level") }) { Text("Level ${card.question.level}") }
                }
                Div({ classes("bar") }) { Div({ style { width(((index + 1) * 100.0 / cards.size).percent) } }) }
                Div({ classes("q") }) { Text(card.question.question) }
                val picked = chosen
                card.answers.forEachIndexed { i, answer ->
                    Button({
                        classes(*listOfNotNull(
                            "answer",
                            if (picked != null && i == card.correctIndex) "right" else null,
                            if (picked != null && i == picked && i != card.correctIndex) "wrong" else null
                        ).toTypedArray())
                        if (picked != null) attr("disabled", "")
                        onClick {
                            if (chosen == null) {
                                chosen = i
                                if (i == card.correctIndex) score++
                            }
                        }
                    }) {
                        Span({ classes("grow") }) { Text(answer) }
                        if (picked != null && (i == card.correctIndex || i == picked)) {
                            Span({ classes("mark") }) { Text(if (i == card.correctIndex) "✓" else "✗") }
                        }
                    }
                }
                if (picked != null) {
                    val right = picked == card.correctIndex
                    Div({ classes("fact") }) {
                        Div({ classes("verdict", if (right) "right" else "wrong") }) { Text(if (right) "Correct! 🎉" else "Not quite!") }
                        Div { Text(card.question.fact) }
                    }
                    Button({ classes("chunky", "next", "center"); onClick { index++; chosen = null } }) {
                        Text(if (index + 1 < cards.size) "Next question" else "See my score")
                    }
                }
            }
        }
    }
}
